import type {
    UseCaseType,
    EvaluationCriterion,
    CallCategory,
    VoiceOption,
} from "@type/agent";

export const USE_CASE_OPTIONS: Record<UseCaseType, { label: string; description: string }> = {
    lead_qualification: { label: "Lead Qualification", description: "Qualify inbound or outbound leads through structured conversations" },
    customer_verification: { label: "Customer Verification", description: "Verify customer identity and account details" },
    support_automation: { label: "Support Automation", description: "Handle common support inquiries and triage issues" },
    appointment_scheduling: { label: "Appointment Scheduling", description: "Schedule and confirm appointments with contacts" },
    survey: { label: "Survey & Feedback", description: "Conduct surveys and collect structured feedback" },
    collections: { label: "Collections", description: "Handle payment reminders and collections conversations" },
    custom: { label: "Custom Workflow", description: "Build a custom voice workflow from scratch" },
};

export const DEFAULT_EVALUATION_CRITERIA: EvaluationCriterion[] = [
    { id: "1", name: "Accuracy", description: "How accurately the contact responded to questions", weight: 25, enabled: true },
    { id: "2", name: "Compliance", description: "Adherence to required scripts and disclosures", weight: 20, enabled: true },
    { id: "3", name: "Intent Match", description: "Whether the contact matches the target intent", weight: 25, enabled: true },
    { id: "4", name: "Risk Signals", description: "Detection of risk indicators or red flags", weight: 15, enabled: true },
    { id: "5", name: "Custom Score", description: "Custom scoring factor for this campaign", weight: 15, enabled: false },
];

export const CALL_CATEGORIES: Record<
    CallCategory,
    { label: string; color: string }
> = {
    qualified_lead: { label: "Qualified Lead", color: "text-emerald-700 bg-emerald-50" },
    interested: { label: "Interested", color: "text-green-700 bg-green-50" },
    not_interested: { label: "Not Interested", color: "text-gray-700 bg-gray-100" },
    callback_requested: { label: "Callback Requested", color: "text-blue-700 bg-blue-50" },
    needs_follow_up: { label: "Needs Follow Up", color: "text-amber-700 bg-amber-50" },
    wrong_number: { label: "Wrong Number", color: "text-orange-700 bg-orange-50" },
    voicemail: { label: "Voicemail", color: "text-purple-700 bg-purple-50" },
    no_answer: { label: "No Answer", color: "text-gray-500 bg-gray-50" },
    do_not_call: { label: "Do Not Call", color: "text-red-700 bg-red-50" },
    completed: { label: "Completed", color: "text-green-700 bg-green-50" },
    other: { label: "Other", color: "text-gray-600 bg-gray-50" },
};

export const AVAILABLE_VOICES: VoiceOption[] = [
    { id: "en-west-f", name: "English (Western) Female", gender: "female", accent: "Western", description: "Natural Western English female voice" },
    { id: "en-west-m", name: "English (Western) Male", gender: "male", accent: "Western", description: "Natural Western English male voice" },
    { id: "en-mysg-f", name: "English (MY/SG) Female", gender: "female", accent: "MY/SG", description: "Malaysian/Singaporean English female voice" },
    { id: "en-mysg-m", name: "English (MY/SG) Male", gender: "male", accent: "MY/SG", description: "Natural Malaysian/Singaporean English male voice" },
    { id: "id-f", name: "Indonesian Female", gender: "female", accent: "Indonesian", description: "Natural Indonesian female voice" },
    { id: "id-m", name: "Indonesian Male", gender: "male", accent: "Indonesian", description: "Natural Indonesian male voice" },
];

export const AVAILABLE_LANGUAGES = [
    { code: "en-west", label: "English (Western)" },
    { code: "en-mysg", label: "English (MY/SG)" },
    { code: "id", label: "Indonesian" },
] as const;
